/**
 * Icon registry — the concept → glyph map for the web client.
 *
 * `docs/design-language.md` §4 and `design/web/archetypes/app-shell.md` §5 fix
 * the rule: **one glyph per concept, app-wide**, and no feature file imports a
 * raw glyph. Everything comes from here, so "homework" is the same mark on every
 * screen — and the same mark mobile's `M0.2` registry will carry, because a
 * concept has one glyph in both clients.
 *
 * The set is Lucide (`lucide-react`), replacing Ant Design's icons. One stroke
 * weight (`STROKE`) and one geometry app-wide; §4's mixed-weight warning is why
 * the width is a single constant and never per-call.
 *
 * ## Sizing — inherited, not fixed
 * Each icon renders as `<span class="anticon"><svg class="lms-icon"/></span>`,
 * exactly the shape antd shipped, and the inner svg is `1em`. So it sizes to the
 * surrounding `font-size` — a 14px button icon, a tile's `--icon-size-*`, a
 * `style={{ fontSize }}` at a call site — precisely as antd's did. The `anticon`
 * wrapper also means antd's own spacing (`.ant-btn .anticon + span`, input affix
 * gaps, `vertical-align`) keeps working untouched. The four §4 sizes — inline 14,
 * control 18, standalone 20, feature 28 — are available as the `size` token when a
 * surface wants an explicit one; most call sites should let it inherit.
 *
 * ## One layer
 * Only **concepts** (`IconAttendance`, `IconEdit`, …). The antd-name
 * compatibility bridge (`EditOutlined = IconEdit`, …) that W4.2 shipped so
 * out-of-scope files could move by swapping an import was retired in W5.1b —
 * every call site now names the concept. `iconRegistry.test.js` fails the build
 * if a raw `lucide-react` import or an antd-era `*Outlined` / `*Filled` /
 * `*TwoTone` name reappears anywhere outside this file.
 */
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, AtSign, Ban, BarChart3, Bell, BookOpen,
  BookOpenText, Bot, Bug, Calculator, Calendar, CalendarClock, Camera, Check,
  ChevronDown, ChevronLeft, ChevronRight, CircleAlert, CircleCheck, CircleHelp,
  CirclePlay, CircleX, ClipboardList, Clock, Cloud, CloudUpload, Contact, Copy,
  CornerDownLeft, CreditCard, Crown, Database, DollarSign, Download, Eraser, Eye,
  EyeOff, File, FileDown, FileImage, FileSpreadsheet, FileText, Flag, Folder,
  FolderPlus, Globe, GraduationCap, GripVertical, Group, Headphones, History, House,
  Image, Import, Inbox, Info, KeyRound, Landmark, LayoutDashboard, LayoutGrid,
  Lightbulb, Link, List, LoaderCircle, Lock, LockOpen, LogIn, LogOut, Maximize,
  MapPin, Megaphone, MessageSquare, Mic, Minus, Monitor, MoreVertical,
  PanelLeftClose, PanelLeftOpen, Paperclip, Pause, Pencil, Percent, Phone, PieChart,
  Play, Plus, RefreshCcw, RefreshCw, Rocket, RotateCcw, RotateCw, Save, ScanLine,
  Search, Send, Server, Settings, Shield, ShieldCheck, Sigma, Smartphone, Smile,
  SquareCheck, SquarePen, Star, Table, TableProperties, Tablet, Tag, TrendingDown,
  TrendingUp, TriangleAlert, Trash2, Trophy, Undo2, Unplug, Upload, User, UserMinus,
  UserPlus, Users, Wallet, Wifi, X, Zap, ZoomIn, ZoomOut,
} from 'lucide-react';

/** One stroke weight app-wide (§4). Lucide's default is 2 at 24; 1.75 reads a
 *  touch lighter and, being a constant, is uniform across every size. */
const STROKE = 1.75;

/** The four §4 sizes, in px, for the optional explicit `size` token. */
export const ICON_SIZE = { inline: 14, control: 18, standalone: 20, feature: 28 };

/**
 * Wrap a Lucide glyph as a drop-in for an antd icon: a `span.anticon` around an
 * `svg.lms-icon` (1em), so it inherits font-size and antd's spacing.
 *
 * Props: `spin` (adds the rotate animation), `size` (a token from ICON_SIZE or a
 * number of px — sets the wrapper font-size), plus the usual `className`,
 * `style`, `onClick`, `title`, aria-* — all forwarded to the wrapper.
 */
const make = (Glyph, glyphProps) => {
  const Icon = ({ spin, size, className, style, ...rest }) => {
    const px = typeof size === 'string' ? ICON_SIZE[size] : size;
    const cls = ['anticon', spin ? 'lms-icon-spin' : '', className].filter(Boolean).join(' ');
    const wrapStyle = px ? { fontSize: px, ...style } : style;
    return (
      <span {...rest} className={cls} style={wrapStyle}>
        <Glyph className="lms-icon" strokeWidth={STROKE} {...glyphProps} />
      </span>
    );
  };
  return Icon;
};

const filled = (Glyph) => make(Glyph, { fill: 'currentColor' });

/* ------------------------------------------------------------------ *
 * Concepts — the map. `app-shell.md` §5 assigns the hue; the glyph is here.
 * ------------------------------------------------------------------ */

// Navigation / domain
export const IconDashboard = make(LayoutDashboard);
export const IconAttendance = make(SquareCheck);        // pistachio
export const IconTuition = make(Wallet);                // terracotta
export const IconCourse = make(BookOpen);
export const IconGroup = make(Group);
export const IconSchedule = make(CalendarClock);
export const IconUsers = make(Users);
export const IconParentLink = make(Contact);
export const IconAnnouncement = make(Megaphone);
export const IconRoom = make(Landmark);
export const IconMockExam = make(FileText);             // plum
export const IconPracticeTest = make(FileText);         // plum
export const IconQuestionBank = make(Database);         // indigo
export const IconAnalytics = make(BarChart3);
export const IconMessage = make(MessageSquare);
export const IconRanking = make(Trophy);                // saffron
export const IconResources = make(BookOpenText);
export const IconProfile = make(User);
export const IconSettings = make(Settings);
export const IconLogin = make(LogIn);
export const IconLogout = make(LogOut);
export const IconHomework = make(ClipboardList);
export const IconLesson = make(GraduationCap);
export const IconQuestionSet = make(FileText);         // indigo — a set of questions
export const IconFolder = make(Folder);                // question-bank levels
export const IconFolderAdd = make(FolderPlus);
export const IconAi = make(Bot);                       // AI generation / usage

// Generic UI
export const IconAdd = make(Plus);
export const IconEdit = make(Pencil);
export const IconDelete = make(Trash2);
export const IconSearch = make(Search);
export const IconClose = make(X);
export const IconMore = make(MoreVertical);
export const IconView = make(Eye);
export const IconHide = make(EyeOff);
export const IconCopy = make(Copy);
export const IconSave = make(Save);
export const IconUpload = make(Upload);
export const IconDownload = make(Download);
export const IconReload = make(RefreshCw);
export const IconSync = make(RefreshCcw);
export const IconLoading = make(LoaderCircle);
export const IconCheck = make(Check);
export const IconInfo = make(Info);
export const IconWarning = make(TriangleAlert);
export const IconError = make(CircleAlert);
export const IconSuccess = make(CircleCheck);
export const IconStop = make(Ban);
export const IconInbox = make(Inbox);
export const IconStar = make(Star);
export const IconStarFilled = filled(Star);
export const IconBell = make(Bell);
export const IconCalendar = make(Calendar);
export const IconClock = make(Clock);
export const IconLink = make(Link);
export const IconKey = make(KeyRound);
export const IconLock = make(Lock);
export const IconUnlock = make(LockOpen);
export const IconPhone = make(Phone);
export const IconWallet = make(Wallet);
export const IconTrophy = make(Trophy);
export const IconList = make(List);
export const IconTable = make(Table);
export const IconGlobe = make(Globe);
export const IconTrophyFilled = filled(Trophy);
export const IconArrowLeft = make(ArrowLeft);
export const IconArrowRight = make(ArrowRight);
export const IconChevronDown = make(ChevronDown);
export const IconChevronLeft = make(ChevronLeft);
export const IconChevronRight = make(ChevronRight);

/* ------------------------------------------------------------------ *
 * Extended UI concepts — the rest of the app's vocabulary, one concept
 * per glyph. W5.1b retired the antd-name compatibility bridge that used
 * to sit here; feature files now import these concept names directly,
 * and the `iconRegistry.test.js` check fails the build if a raw
 * `lucide-react` import or an antd-era `*Outlined`/`*Filled` name comes
 * back anywhere outside this file.
 * ------------------------------------------------------------------ */
export const IconUser = make(User);                    // a person (generic; IconProfile is the destination)
export const IconUsername = make(AtSign);              // a username — the handle, not the person
export const IconUserAdd = make(UserPlus);
export const IconUserRemove = make(UserMinus);
export const IconBank = make(Landmark);
export const IconMoney = make(DollarSign);
export const IconCreditCard = make(CreditCard);
export const IconGrid = make(LayoutGrid);
export const IconDocument = make(FileText);
export const IconFile = make(File);
export const IconFileExcel = make(FileSpreadsheet);
export const IconFileImage = make(FileImage);
export const IconExport = make(FileDown);
export const IconImport = make(Import);
export const IconImage = make(Image);
export const IconPieChart = make(PieChart);
export const IconTrendUp = make(TrendingUp);
export const IconTrendDown = make(TrendingDown);
export const IconHelp = make(CircleHelp);
export const IconCloseCircle = make(CircleX);
export const IconPlay = make(CirclePlay);
export const IconPlayFilled = filled(Play);
export const IconPause = make(Pause);
export const IconSuccessFilled = filled(CircleCheck);
export const IconErrorFilled = filled(CircleAlert);
export const IconWarningFilled = filled(TriangleAlert);
export const IconCheckSquare = make(SquareCheck);
export const IconClear = make(Eraser);
export const IconCloud = make(Cloud);
export const IconServer = make(Server);
export const IconCloudUpload = make(CloudUpload);
export const IconCrown = make(Crown);
export const IconSupport = make(Headphones);
export const IconDesktop = make(Monitor);
export const IconMobile = make(Smartphone);
export const IconTablet = make(Tablet);
export const IconDisconnect = make(Unplug);
export const IconWifi = make(Wifi);
export const IconEnter = make(CornerDownLeft);
export const IconLocation = make(MapPin);
export const IconFlag = make(Flag);
export const IconForm = make(SquarePen);
export const IconFullscreen = make(Maximize);
export const IconFunction = make(Sigma);
export const IconCalculator = make(Calculator);
export const IconHistory = make(History);
export const IconDrag = make(GripVertical);
export const IconHome = make(House);
export const IconTableInsert = make(TableProperties);
export const IconMenuFold = make(PanelLeftClose);
export const IconMenuUnfold = make(PanelLeftOpen);
export const IconMinus = make(Minus);
export const IconAttachment = make(Paperclip);
export const IconPercent = make(Percent);
export const IconRocket = make(Rocket);
export const IconRotateLeft = make(RotateCcw);
export const IconRotateRight = make(RotateCw);
export const IconShieldCheck = make(ShieldCheck);
export const IconShield = make(Shield);
export const IconScan = make(ScanLine);
export const IconSend = make(Send);
export const IconSmile = make(Smile);
export const IconTag = make(Tag);
export const IconZap = make(Zap);
export const IconUndo = make(Undo2);
export const IconMic = make(Mic);
export const IconCamera = make(Camera);
export const IconIdea = make(Lightbulb);
export const IconIdeaFilled = filled(Lightbulb);
export const IconBugFilled = filled(Bug);
export const IconZoomIn = make(ZoomIn);
export const IconZoomOut = make(ZoomOut);
export const IconArrowUp = make(ArrowUp);
export const IconArrowDown = make(ArrowDown);
